import { yupResolver } from "@hookform/resolvers/yup";
import { Icon } from "@iconify/react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import * as yup from "yup";

import CustomCircularProgress from "src/Component/custom/CustomCircularProgress";
import CustomPreviewCard from "src/Component/custom/CustomPreviewCard";
import { useSnackbar } from "src/Component/Snakbar";

// Define validation schema using Yup
const schema = yup.object().shape({
  username: yup.string().required("Username is required"),
  mobile: yup
    .number()
    .typeError("Mobile must be a number")
    .required("Mobile is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  location: yup.string().required("Location is required"),
  image: yup
    .string()
    .nullable()
    .test(
      "is-url-or-empty",
      "Image must be a valid URL",
      (value) => !value || value.startsWith("http") || value.startsWith("blob:")
    ),
  documents: yup
    .array()
    .of(
      yup.object().shape({
        url: yup.string().required("Document URL is required"),
        name: yup.string().required("Document name is required"),
        type: yup.string().required("Document type is required"),
      })
    )
    .min(1, "At least one document is required"),
});

const ProfileForm = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [initialDocuments, setInitialDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      mobile: "",
      email: "",
      location: "",
      image: null,
      documents: [],
    },
  });

  const token = sessionStorage.getItem("authToken");
  const userid = sessionStorage.getItem("userId");
  const [isFetching, setIsFetching] = useState(true);

  const imageValue = watch("image");

  const handleImageChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        const imageUrl = URL.createObjectURL(file);
        setProfileImage(imageUrl);
        setValue("image", imageUrl);
      }
    },
    [setValue]
  );

  const handleFileChange = useCallback(
    (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        const newFiles = files.map((file) => ({
          url: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
          file,
        }));

        setUploadedFiles((prev) => [...prev, ...newFiles]);
        setValue(
          "documents",
          [
            ...initialDocuments,
            ...newFiles.map((f) => ({
              url: f.url,
              name: f.name,
              type: f.type,
            })),
          ],
          {
            shouldValidate: true,
          }
        );
      }
    },
    [initialDocuments, setValue]
  );

  const removeDocument = useCallback(
    (index, isInitialDoc = false) => {
      if (isInitialDoc) {
        setInitialDocuments((prev) => {
          const newDocs = [...prev];
          newDocs.splice(index, 1);
          setValue(
            "documents",
            [
              ...newDocs,
              ...uploadedFiles.map((f) => ({
                url: f.url,
                name: f.name,
                type: f.type,
              })),
            ],
            { shouldValidate: true }
          );
          return newDocs;
        });
      } else {
        setUploadedFiles((prev) => {
          const newFiles = [...prev];
          newFiles.splice(index - initialDocuments.length, 1);

          setValue(
            "documents",
            [
              ...initialDocuments,
              ...newFiles.map((f) => ({
                url: f.url,
                name: f.name,
                type: f.type,
              })),
            ],
            { shouldValidate: true }
          );
          return newFiles;
        });
      }
    },
    [initialDocuments, uploadedFiles, setValue]
  );
  const fetchUserData = useCallback(async () => {
    setIsFetching(true); // start loader

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}student/get-student/${userid}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const { username, email, mobile, location, image, documents } =
        response.data.data;

      setValue("username", username || "");
      setValue("mobile", mobile ? Number(mobile) : "");
      setValue("email", email || "");
      setValue("location", location || "");

      if (image) {
        setProfileImage(image);
        setValue("image", image);
      }

      if (documents && documents.length > 0) {
        const docObjects = documents.map((doc) => ({
          url: doc.url,
          name: doc.name || doc.url.split("/").pop().split("?")[0],
          type: doc.type || getFileTypeFromUrl(doc.url),
        }));
        setInitialDocuments(docObjects);
        setValue("documents", docObjects, { shouldValidate: true });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsFetching(false); // hide loader
    }
  }, [userid, token, setValue]);

  // Helper function to guess file type from URL
  const getFileTypeFromUrl = (url) => {
    const extension = url.split(".").pop().split("?")[0].toLowerCase();
    switch (extension) {
      case "pdf":
        return "application/pdf";
      case "doc":
        return "application/msword";
      case "docx":
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case "xls":
        return "application/vnd.ms-excel";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      case "txt":
        return "text/plain";
      case "jpg":
      case "jpeg":
      case "png":
        return `image/${extension}`;
      default:
        return "application/octet-stream";
    }
  };

  useEffect(() => {
    if (userid) {
      fetchUserData();
    }
  }, [fetchUserData, userid]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    const convertToBase64 = async (file) => {
      if (!(file instanceof Blob)) {
        console.error("Invalid file type for Base64 conversion:", file);
        return file;
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    let imageBase64 = data.image;
    if (profileImage && profileImage.startsWith("blob:")) {
      try {
        const response = await fetch(profileImage);
        const blob = await response.blob();
        imageBase64 = await convertToBase64(blob);
      } catch (error) {
        console.error("Error converting image to Base64:", error);
        showSnackbar("Failed to convert profile image.", "error");
      }
    }

    let documentList = [];
    try {
      documentList = [
        ...initialDocuments.map((doc) => ({ url: doc.url })),
        ...(await Promise.all(
          uploadedFiles.map(async (file) => ({
            url: await convertToBase64(file.file),
          }))
        )),
      ];
    } catch (error) {
      console.error("Error converting documents:", error);
      showSnackbar("Failed to process uploaded documents.", "error");
    }

    try {
      const payload = {
        username: data.username,
        email: data.email,
        mobile: data.mobile,
        location: data.location,
        image: imageBase64,
        documents: documentList,
      };

      const url = `${process.env.REACT_APP_BASE_URL}student/update-profile`;

      await axios.put(url, payload, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      showSnackbar("Profile updated successfully!", "success");
      navigate("/dashboard/home");
    } catch (error) {
      console.error("Error updating profile:", error);
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred during profile update.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderDocumentPreview = () => {
    const allDocuments = [
      ...initialDocuments,
      ...uploadedFiles.map((doc) => ({
        url: doc.url,
        name: doc.name,
        type: doc.type,
      })),
    ];

    if (allDocuments.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Uploaded Documents:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {allDocuments.map((doc, index) => (
            <CustomPreviewCard
              key={`doc-${index}`}
              doc={doc}
              index={index}
              onRemove={() =>
                removeDocument(index, index < initialDocuments.length)
              }
            />
          ))}
        </Stack>
      </Box>
    );
  };

  if (isFetching) {
    return <CustomCircularProgress />;
  }

  return (
    <Box sx={{ padding: "20px", margin: "20px 0" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} justifyContent="center">
          {/* Profile Image */}
          <Grid item xs={12} md={4} container justifyContent="center">
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  bgcolor: "#00999E",
                  p: "10px",
                  borderRadius: "20px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Avatar
                  src={imageValue || profileImage}
                  sx={{ width: "200px", height: "200px" }}
                />
              </Box>
              <Button
                variant="contained"
                component="label"
                sx={{
                  bgcolor: "#D2FEFF",
                  color: "#000",
                  "&:hover": { bgcolor: "#D2FEFF" },
                }}
              >
                Update Profile
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {errors.image && (
                <Typography color="error">{errors.image.message}</Typography>
              )}
            </Box>
          </Grid>

          {/* Form Fields */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {["username", "mobile", "email", "location"].map((field) => (
                <Controller
                  key={field}
                  name={field}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Box mb={3}>
                      <Typography>
                        {field
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder={field
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                        value={value || ""}
                        onChange={onChange}
                        error={!!errors[field]}
                        helperText={errors[field]?.message}
                        type={field === "mobile" ? "number" : "text"}
                        disabled={field === "email"}
                      />
                    </Box>
                  )}
                />
              ))}

              {/* Document Upload */}
              <Typography>Upload Document</Typography>
              <Box
                sx={{
                  border: "1px dashed grey",
                  borderRadius: "5px",
                  bgcolor: "#DEF2F3",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  p: 2,
                  mt: 2,
                }}
              >
                <Icon
                  icon="flowbite:upload-solid"
                  style={{ color: "#80869A", fontSize: "30px" }}
                />
                <Typography variant="h6">
                  Drag and Drop or Select to Upload Documents
                </Typography>
                <Typography variant="caption">
                  Supports PDF, Word, Excel, Images, and more
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    bgcolor: "#fff",
                    width: "150px",
                    border: "1px solid #00999E",
                    color: "#00999E",
                    mt: 2,
                  }}
                >
                  Select File
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
              {errors.documents && (
                <Typography color="error">
                  {errors.documents.message}
                </Typography>
              )}

              {/* Document Preview */}
              {renderDocumentPreview()}

              {/* Submit Button */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    width: "200px",
                    bgcolor: "#00999E",
                    "&:hover": { bgcolor: "#00999E" },
                  }}
                >
                  {isLoading ? "Processing..." : "Update Profile"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ProfileForm;
